import { get } from 'svelte/store';
import { BetLeftCommand, BetRightCommand, DoubleBetCommand, PlaceBetCommand } from './betting-commands';
import { deserialize } from './bettings-serializer';
import { IBettingSystem, ISerializableBetCommand } from './interfaces';
import { currency } from '@src/store/index';

export class BettingSystem implements IBettingSystem {
    public history: ISerializableBetCommand[] = [];
    private bets: number[][] = Array.from({ length: 8 }, () => []);
    private limits: number[] = Array.from({ length: 8 }, () => Number.MAX_VALUE);

    constructor() {}

    public init() {
        this.loadHistory();
    }

    /** Commands */
    executePlaceBetCommand(betId: number, chipSize: number): void {
        if (this.isColorMaxedOut(betId)) return;
        const command = new PlaceBetCommand(this, betId, chipSize);
        command.execute();
        this.history.push(command);
        this.saveHistory();
    }

    executeBetLeftCommand(chipSize: number): void {
        if (this.isLeftSectorMaxedOut()) return;
        const command = new BetLeftCommand(this, chipSize);
        command.execute();
        this.history.push(command);
        this.saveHistory();
    }

    executeBetRightCommand(chipSize: number): void {
        if (this.isRightSectorMaxedOut()) return;
        const command = new BetRightCommand(this, chipSize);
        command.execute();
        this.history.push(command);
        this.saveHistory();
    }

    executeDoubleCommand(): void {
        if (this.isAllMaxedOut()) return;
        const command = new DoubleBetCommand(this);
        command.execute();
        this.history.push(command);
        this.saveHistory();
    }

    executeUndoCommand(): void {
        const command = this.history.pop();
        if (command) {
            command.undo();
            this.saveHistory();
        } else {
            console.log('No more actions to undo.');
        }
    }

    private getBetPerColor() {
        return this.bets.map(bet => bet.reduce((total, bet) => total + bet, 0));
    }

    /** Public */
    getLimitedBetPerColor() {
        const betPerColor = this.getBetPerColor();
        for (let i = 0; i < betPerColor.length; i++) {
            if (this.limits[i] < betPerColor[i]) {
                betPerColor[i] = this.limits[i];
            }
        }
        return betPerColor;
    }

    reset() {
        this.history = [];
        this.bets = Array.from({ length: 8 }, () => []);
        this.saveHistory();
    }

    getBets(): number[][] {
        return this.bets;
    }

    setBets(bets: number[][]) {
        this.bets = bets;
    }

    setLimits(limits: number[]) {
        this.limits = limits;
    }

    getHistory(): ISerializableBetCommand[] {
        return this.history;
    }

    getHistorySize(): number {
        return this.history.length;
    }

    /** internal */
    doubleBets(): void {
        this.bets = this.bets.map(betArray => betArray.flatMap(bet => [bet, bet]));
    }

    halveBets(): void {
        this.bets = this.bets.map(betArray => {
            const halfArray = [];
            for (let i = 0; i < betArray.length; i += 2) {
                halfArray.push(betArray[i]);
            }
            return halfArray;
        });
    }

    addBet(betId: number, chipSize: number): void {
        if (betId >= 0 && betId < this.bets.length) {
            this.bets[betId].push(chipSize);
        }
    }

    removeLastBet(betId: number): void {
        if (betId >= 0 && betId < this.bets.length) {
            const gameBets = this.bets[betId];
            if (gameBets.length > 0) {
                gameBets.pop();
            }
        }
    }

    /** private */
    private isAllMaxedOut(): boolean {
        return this.getBetPerColor().every((betAmount, index) => betAmount >= this.limits[index]);
    }

    private isColorMaxedOut(index: number): boolean {
        return this.getBetPerColor()[index] >= this.limits[index];
    }

    private isLeftSectorMaxedOut(): boolean {
        const arr = [0, 1, 2, 3];
        const bets = this.getBetPerColor();
        return arr.every(i => bets[i] >= this.limits[i]);
    }

    private isRightSectorMaxedOut(): boolean {
        const arr = [4, 5, 6, 7];
        const bets = this.getBetPerColor();
        return arr.every(i => bets[i] >= this.limits[i]);
    }

    private saveHistory(): void {
        const history = this.history.map(command => command.serialize());
        const serialized = JSON.stringify(history);
        console.log('serialized betting system: ' + serialized);
        const c = get(currency) || (Math.random() + '');
        localStorage.setItem('bettingSystemHistory' + c, serialized);
    }

    private loadHistory(): void {
        if (!window.localStorage) return;
        const c = get(currency) || (Math.random() + '');
        const history = localStorage.getItem('bettingSystemHistory' + c);
        if (history) {
            const historyArray = JSON.parse(history);
            const previousCommands = historyArray.map((data: object) => deserialize(this, data));
            for (const command of previousCommands) {
                command.execute();
                this.history.push(command);
            }
        }
    }
}
